import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Animate } from "react-animate-mount";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Stepper from "components/stepper/Stepper";
import SurveyOverview from "components/surveys/SurveyOverview";
import useSurvey from "hooks/surveys/useSurvey";
import useSurveyForm from "hooks/surveys/useSurveyForm";
import SurveyView from "components/surveys/SurveyView";
import TextEditor from "components/textEditor/TextEditor";
import AddChoicesStep from "components/surveys/AddChoicesStep";

const ManageSurvey = ({ isEditable }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const { current, updateSurvey, createSurvey, deleteSurvey } = useSurvey({
    id,
  });
  const { errors, FIELDS, values, setFieldValue, handleSubmit } = useSurveyForm(
    {
      id,
      survey: current || {},
      create: !id ? createSurvey : () => {},
      update: id ? updateSurvey : () => {},
    },
  );
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const steps = [
    {
      id: 1,
      title: "Step 1",
      desc: t("surveys.general"),
      content: (
        <SurveyOverview
          errors={{
            title: errors[FIELDS.TITLE],
            description: errors[FIELDS.DESCRIPTION],
            categories: errors[FIELDS.CATEGORIES],
          }}
          overview={{
            title: values[FIELDS.TITLE],
            description: values[FIELDS.DESCRIPTION],
            categories: values[FIELDS.CATEGORIES],
            published: values[FIELDS.PUBLISHED],
            level: values[FIELDS.LEVEL],
            deadline: values[FIELDS.DEADLINE],
            endOfVoting: values[FIELDS.END_OF_VOTING],
            image: values[FIELDS.IMAGE],
          }}
          setFieldValue={setFieldValue}
          deleteSurvey={deleteSurvey}
          id={id}
        />
      ),
    },
    {
      id: 2,
      title: "Step 2",
      desc: t("predictors.content"),
      content: (
        <div className="pb-20">
          <TextEditor
            isEditable={isEditable}
            setValue={(value) => {
              setFieldValue(FIELDS.CONTENT, value);
            }}
            value={values[FIELDS.CONTENT]}
            height="100%"
            width="100%"
          />
        </div>
      ),
    },
    {
      id: 3,
      title: "Step 3",
      desc: "Տարբերականներ",
      content: (
        <div className="pb-20">
          <AddChoicesStep
            choices={values[FIELDS.CHOICES]}
            onChange={(value) => setFieldValue(FIELDS.CHOICES, value)}
          />
        </div>
      ),
    },
    {
      id: 4,
      title: "Step 4",
      desc: t("surveys.review"),
      content: <SurveyView isEditable={isEditable} survey={values} />,
    },
    {
      id: 5,
      title: "Step 5",
      desc: t("surveys.publish"),
      content: (
        <div className="flex items-center justify-center text-green-600 text-[32px]">
          Հրապարակված է
        </div>
      ),
    },
  ];

  return (
    <div className="mt-10 flex flex-col justify-center">
      <Animate show={isMounted}>
        <Stepper
          steps={steps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isWithErrors={Object.keys(errors).length}
        />
      </Animate>
      <div className="flex w-full justify-between mt-10">
        {activeStep < steps.length - 1 && (
          <div
            onClick={() => activeStep > 0 && setActiveStep(activeStep - 1)}
            className={`p-2 bg-opacity-50 z-50 flex hover:bg-gray-400 text-gray-600 hover:text-white ${
              activeStep > 0 ? "opacity-100 cursor-pointer" : "opacity-0"
            }`}
          >
            <FaChevronLeft size={32} color="" />
          </div>
        )}
        <div className="w-full">
          {steps.map((step, index) => {
            return (
              <Animate show={index === activeStep} key={index}>
                {step.content}
              </Animate>
            );
          })}
        </div>
        {activeStep <= 3 &&
          !Object.keys(errors).length &&
          values[FIELDS.TITLE] && (
            <div
              onClick={() => {
                if (activeStep === 3) {
                  setActiveStep(activeStep + 1);
                  handleSubmit();
                } else {
                  setActiveStep(activeStep + 1);
                }
              }}
              className={`p-2 bg-opacity-50 cursor-pointer flex justify-end hover:bg-gray-400 text-gray-600 hover:text-white ${
                steps.length !== activeStep ? "opacity-100" : "opacity-0"
              }`}
            >
              <FaChevronRight
                size={32}
                color={activeStep === 3 ? "#2E38FF" : ""}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default ManageSurvey;
